/**
 * Axios instance shared by all service modules.
 *
 * Auth strategy: HttpOnly cookies (set by the backend on login/register/refresh).
 *  - access_token  cookie: short-lived (15 min), sent with every API request automatically.
 *  - refresh_token cookie: long-lived (7 days), path-restricted to /auth/refresh only.
 *  - is_authenticated: a plain localStorage flag (NOT the token) used only to decide
 *    whether to call /auth/me on page load, avoiding an unnecessary network round-trip
 *    for users who are definitely not logged in.
 *
 * Spring Boot response envelope:
 *   { success: boolean, data: T, message?: string, validationErrors?: object }
 *
 * Paginated response (inside `data`):
 *   { content: T[], totalElements, totalPages, currentPage, pageSize, hasNext, hasPrevious }
 */

import axios from 'axios';

// ─── Auth flag helpers ────────────────────────────────────────────────────────
// These store NO secret — just a hint that a session cookie exists.

const AUTH_FLAG = 'is_authenticated';

export const isAuthenticated = () => localStorage.getItem(AUTH_FLAG) === '1';
export const setAuthenticated = () => localStorage.setItem(AUTH_FLAG, '1');
export const clearAuthenticated = () => localStorage.removeItem(AUTH_FLAG);

// ─── Axios instance ───────────────────────────────────────────────────────────

const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api/v1';

export const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
  paramsSerializer: (params) => {
    const parts = [];
    for (const [key, value] of Object.entries(params || {})) {
      if (value === undefined || value === null) continue;
      if (Array.isArray(value)) {
        value.forEach((v) => parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`));
      } else {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    }
    return parts.join('&');
  },
});

// ─── Token refresh state ──────────────────────────────────────────────────────

let isRefreshing = false;
let refreshQueue = []; // [{ resolve, reject }]

const processQueue = (error = null) => {
  refreshQueue.forEach(({ resolve, reject }) => (error ? reject(error) : resolve()));
  refreshQueue = [];
};

const buildAppError = (error) => {
  const message = !error?.response
    ? 'Unable to connect. Please check your connection and try again.'
    : (error.response.data?.message || error.message || 'Request failed');
  const err = new Error(message);
  err.status = error?.response?.status;
  err.data = error?.response?.data;
  err.validationErrors = error?.response?.data?.validationErrors;
  return err;
};

const redirectToLogin = () => {
  const returnUrl = window.location.pathname + window.location.search;
  window.location.href = `/Login?returnUrl=${encodeURIComponent(returnUrl)}`;
};

// ─── Request interceptor ──────────────────────────────────────────────────────
// Axios 1.x serialises FormData to JSON when the instance default Content-Type
// is application/json. Clearing the header for FormData requests lets the
// browser set the correct multipart/form-data; boundary=... automatically.
api.interceptors.request.use((config) => {
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

// ─── Response interceptor ─────────────────────────────────────────────────────
// Unwrap ApiResponse<T> → T, and auto-refresh JWT on 401

api.interceptors.response.use(
  (response) => {
    const body = response.data;
    if (body && typeof body === 'object' && 'success' in body) {
      return body.data ?? null;
    }
    return body;
  },
  async (error) => {
    const orig = error?.config;
    const status = error?.response?.status;

    if (!orig) return Promise.reject(buildAppError(error));

    const isAuthEndpoint = orig.url?.includes('/auth/');

    if (status === 401 && !orig._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        })
          .then(() => api(orig))
          .catch((queueError) => Promise.reject(buildAppError(queueError)));
      }

      orig._retry = true;
      isRefreshing = true;

      try {
        await axios.post(`${BASE_URL}/auth/refresh`, null, {
          withCredentials: true,
          timeout: 10000,
        });
        processQueue();
        return api(orig);
      } catch (refreshError) {
        processQueue(refreshError);
        const refreshStatus = refreshError?.response?.status;
        if (refreshStatus === 401 || refreshStatus === 403) {
          clearAuthenticated();
          redirectToLogin();
        }
        return Promise.reject(buildAppError(refreshError));
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(buildAppError(error));
  }
);

// ─── Paged response helper ────────────────────────────────────────────────────
// Spring Boot returns { content: [], totalElements, ... } for paginated queries.
// Components typically iterate the array, so we surface `content` directly.
// Attach `._paged` with pagination meta for components that need it.

export function toList(result) {
  if (!result) return [];
  if (Array.isArray(result.content)) {
    const arr = [...result.content];
    arr._paged = {
      totalElements: result.totalElements,
      totalPages:    result.totalPages,
      currentPage:   result.currentPage,
      pageSize:      result.pageSize,
      hasNext:       result.hasNext,
      hasPrevious:   result.hasPrevious,
    };
    return arr;
  }
  return Array.isArray(result) ? result : [];
}

// ─── Sort param helper ────────────────────────────────────────────────────────
// Converts "-created_date" → { sortBy: 'created_date', sortDir: 'DESC' }

export function sortParams(orderBy, limit) {
  const p = {};
  if (limit) p.size = limit;
  if (orderBy) {
    const desc = orderBy.startsWith('-');
    p.sortBy = desc ? orderBy.slice(1) : orderBy;
    p.sortDir = desc ? 'DESC' : 'ASC';
  }
  return p;
}
